import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import config from '../config';

// User interface matching backend response
interface User {
  id: string;
  email: string;
  full_name: string;
  profile_picture?: string;
  user_type: 'student' | 'professional';
  grade?: string;
  email_verified: boolean;
  created_at: string;
  last_login: string;
}

// Session information from backend
interface SessionInfo {
  expiresAt: string;
  minutesUntilExpiry: number;
  idleMinutes: number;
  shouldWarn: boolean;
  warningThreshold: number;
  timeoutThreshold: number;
}

// Auth state interface
interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  sessionInfo: SessionInfo | null;
}

// Auth context value interface
interface AuthContextValue extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  loginWithGoogle: () => void;
  logout: () => Promise<void>;
  checkSession: () => Promise<void>;
  clearError: () => void;
  refreshSession: () => Promise<void>;
}

// Create context with undefined default
const AuthContext = createContext<AuthContextValue | undefined>(undefined);

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// AuthProvider component
interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, setState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
    error: null,
    sessionInfo: null,
  });

  // Get token from localStorage
  const getToken = useCallback(() => {
    return localStorage.getItem('authToken');
  }, []);

  // Set token in localStorage
  const setToken = useCallback((token: string) => {
    localStorage.setItem('authToken', token);
  }, []);

  // Remove token from localStorage
  const removeToken = useCallback(() => {
    localStorage.removeItem('authToken');
  }, []);

  // Clear error
  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }));
  }, []);

  // Check session validity
  const checkSession = useCallback(async () => {
    const token = getToken();
    
    if (!token) {
      setState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
        sessionInfo: null,
      });
      return;
    }

    try {
      const response = await fetch(`${config.backendUrl}/auth/session`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (data.success && data.authenticated) {
        setState({
          user: data.user,
          isAuthenticated: true,
          isLoading: false,
          error: null,
          sessionInfo: data.session,
        });
      } else {
        // Session invalid or expired
        removeToken();
        setState({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          error: data.message || 'Session expired',
          sessionInfo: null,
        });
      }
    } catch (error) {
      console.error('Session check error:', error);
      removeToken();
      setState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: 'Failed to verify session',
        sessionInfo: null,
      });
    }
  }, [getToken, removeToken]);

  // Refresh session activity
  const refreshSession = useCallback(async () => {
    const token = getToken();
    
    if (!token) {
      return;
    }

    try {
      await fetch(`${config.backendUrl}/auth/session/refresh`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
    } catch (error) {
      console.error('Session refresh error:', error);
    }
  }, [getToken]);

  // Login with email and password
  const login = useCallback(async (email: string, password: string) => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const response = await fetch(`${config.backendUrl}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (data.success && data.token) {
        setToken(data.token);
        setState({
          user: data.user,
          isAuthenticated: true,
          isLoading: false,
          error: null,
          sessionInfo: null,
        });
        // Check session to get session info
        await checkSession();
      } else {
        setState(prev => ({
          ...prev,
          isLoading: false,
          error: data.message || 'Login failed',
        }));
      }
    } catch (error) {
      console.error('Login error:', error);
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: 'Failed to login. Please try again.',
      }));
    }
  }, [setToken, checkSession]);

  // Login with Google OAuth
  const loginWithGoogle = useCallback(() => {
    // Redirect to backend Google OAuth endpoint
    window.location.href = `${config.apiUrl}/api/auth/google`;
  }, []);

  // Logout
  const logout = useCallback(async () => {
    const token = getToken();
    
    setState(prev => ({ ...prev, isLoading: true }));

    try {
      if (token) {
        await fetch(`${config.backendUrl}/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Always clear local state and token
      removeToken();
      setState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
        sessionInfo: null,
      });
    }
  }, [getToken, removeToken]);

  // Check session on mount
  useEffect(() => {
    checkSession();
  }, [checkSession]);

  // Set up periodic session check (every 5 minutes)
  useEffect(() => {
    if (state.isAuthenticated) {
      const interval = setInterval(() => {
        checkSession();
      }, 5 * 60 * 1000); // 5 minutes

      return () => clearInterval(interval);
    }
  }, [state.isAuthenticated, checkSession]);

  // Set up activity tracking for session refresh
  useEffect(() => {
    if (state.isAuthenticated) {
      const handleActivity = () => {
        refreshSession();
      };

      // Refresh session on user activity
      const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
      
      // Throttle activity tracking to once per minute
      let lastRefresh = Date.now();
      const throttledHandler = () => {
        const now = Date.now();
        if (now - lastRefresh > 60 * 1000) { // 1 minute
          lastRefresh = now;
          handleActivity();
        }
      };

      events.forEach(event => {
        window.addEventListener(event, throttledHandler);
      });

      return () => {
        events.forEach(event => {
          window.removeEventListener(event, throttledHandler);
        });
      };
    }
  }, [state.isAuthenticated, refreshSession]);

  const value: AuthContextValue = {
    ...state,
    login,
    loginWithGoogle,
    logout,
    checkSession,
    clearError,
    refreshSession,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
